import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Search, Plus, Minus, ArrowRight, HelpCircle, Phone, MessageSquare } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { faqs } from '../mock';

const topics = [
  { title: 'Making a claim', icon: 'ShieldCheck', color: 'bg-[#FFEDD6]' },
  { title: 'Your policy', icon: 'FileText', color: 'bg-[#FCE0EC]' },
  { title: 'Billing & payments', icon: 'CreditCard', color: 'bg-[#E8DDFF]' },
  { title: 'Book a repair', icon: 'Wrench', color: 'bg-[#FFE0D6]' },
  { title: 'Manage account', icon: 'User', color: 'bg-[#FFEDD6]' },
  { title: 'Complaints', icon: 'MessageSquare', color: 'bg-[#FCE0EC]' },
];

const Help = () => {
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(0);
  const filtered = faqs.filter((f) => f.q.toLowerCase().includes(q.toLowerCase()) || f.a.toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="bg-dhs min-h-screen">
      <Header />
      <main>
        <section className="py-14 md:py-20">
          <div className="max-w-4xl mx-auto px-5 text-center">
            <div className="inline-flex items-center gap-2 bg-white border border-[#1F0068]/10 rounded-full px-3.5 py-1.5 text-xs font-semibold text-[#1F0068] mb-5">
              <HelpCircle className="w-3.5 h-3.5" /> Help centre
            </div>
            <h1 className="text-4xl md:text-6xl font-bold text-[#1F0068] tracking-tight leading-[1.05]">
              How can we help?
            </h1>
            <div className="mt-8 relative">
              <Search className="w-5 h-5 absolute left-5 top-1/2 -translate-y-1/2 text-[#1F0068]/50" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search for repairs, claims, billing…"
                className="h-16 pl-14 pr-4 rounded-full text-lg bg-white border-[#1F0068]/10 shadow-lg" />
            </div>
          </div>
        </section>

        <section className="pb-10">
          <div className="max-w-6xl mx-auto px-5">
            <h2 className="text-2xl font-bold text-[#1F0068] mb-6">Popular topics</h2>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {topics.map((t) => (
                <a key={t.title} href="#topic" className="dhs-card bg-white rounded-2xl p-5 border border-[#1F0068]/5 group flex items-center gap-3">
                  <div className={`w-11 h-11 rounded-xl ${t.color} flex items-center justify-center text-[#1F0068]`}>
                    <HelpCircle className="w-5 h-5" />
                  </div>
                  <div className="flex-1 font-bold text-[#1F0068]">{t.title}</div>
                  <ArrowRight className="w-4 h-4 text-[#FF5C39] opacity-60 group-hover:opacity-100 group-hover:translate-x-0.5 transition-all" />
                </a>
              ))}
            </div>
          </div>
        </section>

        <section className="py-16">
          <div className="max-w-4xl mx-auto px-5">
            <h2 className="text-2xl md:text-3xl font-bold text-[#1F0068] mb-6">Frequently asked questions</h2>
            <div className="space-y-3">
              {filtered.length === 0 && (
                <div className="bg-white rounded-2xl p-8 text-center text-[#1F0068]/60">No results found. Try a different search.</div>
              )}
              {filtered.map((f, i) => (
                <div key={f.q} className="rounded-2xl bg-white border border-[#1F0068]/5 overflow-hidden">
                  <button onClick={() => setOpen(open === i ? -1 : i)} className="w-full flex items-center gap-4 p-5 text-left">
                    <span className="flex-1 font-bold text-[#1F0068]">{f.q}</span>
                    <span className={`shrink-0 w-9 h-9 rounded-full flex items-center justify-center ${open === i ? 'bg-[#FF5C39] text-white' : 'bg-[#FFF6EB] text-[#1F0068]'}`}>
                      {open === i ? <Minus className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
                    </span>
                  </button>
                  <div className={`grid transition-all duration-300 ${open === i ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'}`}>
                    <div className="overflow-hidden">
                      <p className="px-5 pb-6 text-[#1F0068]/75 leading-relaxed">{f.a}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="pb-20">
          <div className="max-w-4xl mx-auto px-5">
            <div className="bg-[#1F0068] text-white rounded-3xl p-8 md:p-10 grid md:grid-cols-2 gap-6 items-center">
              <div>
                <h3 className="text-2xl font-bold">Still need help?</h3>
                <p className="mt-2 text-white/80 text-sm">Our UK-based support team is here 24 hours a day, every day of the year.</p>
              </div>
              <div className="flex flex-wrap gap-3 md:justify-end">
                <a href="tel:01135487557"><Button className="rounded-full bg-white text-[#1F0068] hover:bg-[#FFEDD6] gap-1.5"><Phone className="w-4 h-4" /> 0113 548 7557</Button></a>
                <Link to="/contact"><Button className="rounded-full bg-[#FF5C39] hover:bg-[#e0492b] text-white gap-1.5"><MessageSquare className="w-4 h-4" /> Send a message</Button></Link>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default Help;
